import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, Wallet, BarChart3, Library, Star, TrendingUp, Rocket, ArrowRight, Search, Radio } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/components/PlanGate";

const CATEGORIES = [
  {
    key: "eventos", title: "Eventos", icon: CalendarDays, color: "#FF4D00",
    desc: "Gestão avançada de eventos e profissionais.",
    items: [
      { label: "Criar e gerenciar eventos", to: "/eventos/novo" },
      { label: "Meus eventos e candidaturas", to: "/eventos" },
    ],
  },
  {
    key: "equipe", title: "Equipe", icon: Users, color: "#00F0FF",
    desc: "Gestão dos músicos/profissionais dos seus eventos.",
    items: [
      { label: "Buscar e convidar profissionais", to: "/eventos", feature: "contractor_basic" },
      { label: "Prioridade na busca de profissionais", to: "/eventos", feature: "professional_search_priority" },
    ],
  },
  {
    key: "pagamentos", title: "Pagamentos", icon: Wallet, color: "#22c55e",
    desc: "Controles financeiros e acompanhamento dos pagamentos.",
    items: [
      { label: "Pagamentos e repasses Pix", to: "/pagamentos" },
    ],
  },
  {
    key: "relatorios", title: "Relatórios", icon: BarChart3, color: "#a855f7",
    desc: "Indicadores e relatórios relacionados à contratação.",
    items: [
      { label: "Relatórios completos", to: "/relatorios", feature: "complete_reports" },
      { label: "Exportar Excel / CSV / PDF", to: "/relatorios", feature: "export_excel" },
    ],
  },
  {
    key: "repertorio", title: "Repertório", icon: Library, color: "#38bdf8",
    desc: "Recursos de repertório e Modo Palco quando disponíveis.",
    items: [
      { label: "Meu repertório e grupos", to: "/repertorio", feature: "repertoire" },
    ],
  },
  {
    key: "avaliacoes", title: "Avaliações", icon: Star, color: "#f59e0b",
    desc: "Acompanhamento de profissionais e avaliações.",
    items: [
      { label: "Ranking e reputação", to: "/ranking", feature: "ranking_full" },
    ],
  },
  {
    key: "financeiro", title: "Financeiro", icon: TrendingUp, color: "#10b981",
    desc: "Visão consolidada de valores pagos, pendentes e futuros.",
    items: [
      { label: "Resultado financeiro por evento", to: "/relatorios", feature: "event_financial_result" },
      { label: "Histórico financeiro avançado", to: "/relatorios", feature: "advanced_financial_history" },
    ],
  },
  {
    key: "plantao", title: "Modo Plantão", icon: Radio, color: "#ef4444",
    desc: "Disponibilidade em tempo real para chamadas de última hora.",
    items: [
      { label: "Ativar Modo Plantão", to: "/plantao", feature: "oncall" },
    ],
  },
];

export default function ContractorAdvanced() {
  const { hasFeature } = useAuth();
  const navigate = useNavigate();

  if (!hasFeature("contractor_advanced")) return <PlanGate feature="contractor_advanced" title="Recursos Avançados do Contratante" />;

  const cats = CATEGORIES
    .map((c) => ({ ...c, items: c.items.filter((it) => !it.feature || hasFeature(it.feature)) }))
    .filter((c) => c.items.length > 0);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="contractor-advanced-page">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl sm:text-5xl font-semibold tracking-tight flex items-center gap-3"><Rocket className="h-8 w-8 text-[#FF4D00]" />Recursos Avançados do Contratante</h1>
        <p className="text-zinc-400 mt-1 text-sm">Tudo o que você precisa para criar e administrar eventos com alto padrão — reunido em um só lugar. São exibidos apenas os recursos disponíveis no seu plano.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cats.map((c) => (
          <div key={c.key} data-testid={`ca-cat-${c.key}`} className="bg-[#121217] border border-border rounded-sm p-5 flex flex-col hover:border-[#FF4D00]/30 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${c.color}1A`, border: `1px solid ${c.color}44` }}>
                <c.icon className="h-5 w-5" style={{ color: c.color }} />
              </div>
              <div>
                <div className="font-head text-lg font-semibold">{c.title}</div>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mb-3">{c.desc}</p>
            <div className="space-y-1.5 mt-auto">
              {c.items.map((it, i) => (
                <button key={i} data-testid={`ca-item-${c.key}-${i}`} onClick={() => navigate(it.to)}
                  className="w-full flex items-center justify-between gap-2 text-left text-sm text-zinc-200 bg-[#09090B] border border-border rounded-sm px-3 py-2 hover:border-[#FF4D00]/40 hover:text-white transition-colors">
                  <span>{it.label}</span><ArrowRight className="h-4 w-4 text-zinc-500 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
